import { cx } from '@emotion/css';
import React, { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { ix } from '../../../../utils/ix';
import { Button } from '../../../atoms';
import { DateField } from '../../formFields/DateField';
import { StaticDateRangePicker } from '../StaticDateRangePicker';
import { useDefaultRanges } from './ComplexStaticDateRangePicker.hooks';
import { areSameRanges, convertErrorToAcceptedForm, getInitDateRange, isCustom, } from './ComplexStaticDateRangePicker.service';
import { useStyles } from './ComplexStaticDateRangePicker.styles';
import { DatePickerClearIcon } from './components/DatePickerClearIcon';
import { RangesList } from './components/RangesList';
export const ComplexStaticDateRangePicker = memo(function (props) {
    const { isDateRangeRequired = true, onCancel, onChange, onError, onUpdate, ranges, value } = props;
    const classes = useStyles();
    const [initialType, setInitialType] = useState(value.type);
    const [initialDateRange, setInitialDateRange] = useState(getInitDateRange(value, ranges));
    const [temporaryType, setTemporaryType] = useState(initialType);
    const [temporaryDateRange, setTemporaryDateRange] = useState(initialDateRange);
    const [temporaryDateRangeError, setTemporaryDateRangeError] = useState([null, null]);
    const [lastUpdateInInputsOrRangeSelection, setLastUpdateInInputsOrRangeSelection] = useState(Date.now());
    const [focusedDateFieldIndex, setFocusedDateFieldIndex] = useState(-1);
    const defaultRanges = useDefaultRanges();
    const predefinedRangesConfiguration = ranges || defaultRanges;
    const keyToForceRerenderDatePickerIfNewValueIsSelected = useMemo(() => lastUpdateInInputsOrRangeSelection.toString(), [lastUpdateInInputsOrRangeSelection]);
    useEffect(() => {
        const newValue = {
            type: value.type,
            dateRange: value.dateRange,
        };
        setInitialType(newValue.type);
        setInitialDateRange(newValue.dateRange);
        setTemporaryType(newValue.type);
        setTemporaryDateRange(newValue.dateRange);
    }, [value]);
    const handleDateRangePickerOnChange = useCallback((dateRange, keyboardInputValue) => {
        const newValue = {
            type: 'custom',
            dateRange,
        };
        if (onChange) {
            onChange(newValue, keyboardInputValue);
        }
        setTemporaryType(newValue.type);
        setTemporaryDateRange(newValue.dateRange);
    }, [onChange]);
    const handleOnCancelClick = useCallback(() => {
        if (onCancel) {
            onCancel();
        }
        setTemporaryType(initialType);
        setTemporaryDateRange(initialDateRange);
    }, [initialDateRange, initialType, onCancel]);
    const handleOnUpdateClick = useCallback(() => {
        const confirmedValue = {
            type: temporaryType,
            dateRange: temporaryDateRange,
        };
        if (onUpdate) {
            onUpdate(confirmedValue);
        }
        setInitialType(confirmedValue.type);
        setInitialDateRange(confirmedValue.dateRange);
    }, [onUpdate, temporaryDateRange, temporaryType]);
    const handleStartDateInputChange = useCallback((date) => {
        const newValue = {
            type: 'custom',
            dateRange: [date, temporaryDateRange[1]],
        };
        if (onChange) {
            onChange(newValue);
        }
        setTemporaryType(newValue.type);
        setTemporaryDateRange(newValue.dateRange);
        setLastUpdateInInputsOrRangeSelection(Date.now());
    }, [onChange, temporaryDateRange]);
    const handleEndDateInputChange = useCallback((date) => {
        const newValue = {
            type: 'custom',
            dateRange: [temporaryDateRange[0], date],
        };
        if (onChange) {
            onChange(newValue);
        }
        setTemporaryType(newValue.type);
        setTemporaryDateRange(newValue.dateRange);
        setLastUpdateInInputsOrRangeSelection(Date.now());
    }, [onChange, temporaryDateRange]);
    const handleRangesListChange = useCallback((rangeVariant) => {
        if (isCustom(rangeVariant)) {
            const newValue = {
                type: rangeVariant,
                dateRange: temporaryDateRange,
            };
            if (onChange) {
                onChange(newValue);
                setTemporaryType(newValue.type);
                setLastUpdateInInputsOrRangeSelection(Date.now());
            }
        }
        else {
            const rangeConfig = predefinedRangesConfiguration.find((range) => range.type === rangeVariant);
            const newValue = {
                type: rangeVariant,
                dateRange: rangeConfig.getValue(),
            };
            if (onChange) {
                onChange(newValue);
            }
            setTemporaryType(newValue.type);
            setTemporaryDateRange(newValue.dateRange);
            setLastUpdateInInputsOrRangeSelection(Date.now());
        }
    }, [onChange, predefinedRangesConfiguration, temporaryDateRange]);
    const handleClearIconClick = useCallback((dateRangeValueIndex) => () => {
        const newTemporaryDateRange = [...temporaryDateRange];
        newTemporaryDateRange[dateRangeValueIndex] = null;
        setTemporaryDateRange(newTemporaryDateRange);
    }, [temporaryDateRange]);
    const renderDatePickersClearIcon = useCallback((dateRangeValueIndex) => {
        return React.createElement(DatePickerClearIcon, { onClick: handleClearIconClick(dateRangeValueIndex) });
    }, [handleClearIconClick]);
    const handleDateRangeError = useCallback((error, currentValue) => {
        if (onError) {
            onError(error, currentValue);
        }
        setTemporaryDateRangeError(error);
    }, [onError]);
    const handleFocusDateField = useCallback((index) => () => {
        setFocusedDateFieldIndex(index);
    }, []);
    const handleBlurDateField = useCallback(() => {
        setFocusedDateFieldIndex(-1);
    }, []);
    const hasNoChanges = isCustom(initialType)
        ? temporaryType === initialType && areSameRanges(temporaryDateRange, initialDateRange)
        : temporaryType === initialType;
    const hasAnyErrors = !!temporaryDateRangeError[0] || !!temporaryDateRangeError[1];
    const notEveryDateIsSelected = isDateRangeRequired ? !temporaryDateRange[0] || !temporaryDateRange[1] : false;
    return (React.createElement("div", { className: cx(classes.root, props.className), id: props.id },
        React.createElement("div", { className: classes.leftColumn },
            React.createElement(RangesList, { id: ix(props.id, 'predefined-ranges'), onChange: handleRangesListChange, ranges: predefinedRangesConfiguration, value: temporaryType })),
        React.createElement("div", { className: classes.rightColumn },
            React.createElement("div", { className: classes.rightColumnInputs },
                React.createElement(DateField, { InputFieldProps: {
                        customEndAdornment: focusedDateFieldIndex === 0 ? renderDatePickersClearIcon(0) : undefined,
                        error: props.hideErrors ? undefined : convertErrorToAcceptedForm(temporaryDateRangeError[0]),
                    }, id: ix(props.id, 'start-date-input'), onChange: handleStartDateInputChange, onBlur: handleBlurDateField, onFocus: handleFocusDateField(0), value: temporaryDateRange[0] }),
                React.createElement(DateField, { InputFieldProps: {
                        customEndAdornment: focusedDateFieldIndex === 1 ? renderDatePickersClearIcon(1) : undefined,
                        error: props.hideErrors ? undefined : convertErrorToAcceptedForm(temporaryDateRangeError[1]),
                    }, id: ix(props.id, 'end-date-input'), onChange: handleEndDateInputChange, onBlur: handleBlurDateField, onFocus: handleFocusDateField(1), value: temporaryDateRange[1] })),
            React.createElement(StaticDateRangePicker, { key: keyToForceRerenderDatePickerIfNewValueIsSelected, disableFuture: props.disableFuture, disablePast: props.disablePast, id: ix(props.id, 'inner-date-range-picker'), onError: handleDateRangeError, maxDate: props.maxDate, minDate: props.minDate, onChange: handleDateRangePickerOnChange, value: temporaryDateRange }),
            !props.hideButtons && (React.createElement("div", { className: classes.rightColumnButtons },
                React.createElement(Button, { variant: "outlined", id: ix(props.id, 'cancel-btn'), onClick: handleOnCancelClick }, "Cancel"),
                React.createElement(Button, { variant: "contained", id: ix(props.id, 'update-btn'), onClick: handleOnUpdateClick, disabled: hasNoChanges || hasAnyErrors || notEveryDateIsSelected }, "Update"))))));
});
//# sourceMappingURL=ComplexStaticDateRangePicker.js.map